import React, { FC, JSX, ChangeEvent } from 'react';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Select from '../ui/Select';
import { CompanyRegistrationFormData, CompanyRegistrationStepProps, CompanyUser } from '../../types/components.d';
import FileUploader from '../ui/FileUploader';
import { useCompany } from '../../context/CompanyContext';
import { Attachments } from '../../types/api';
import useDebounce from '../../hooks/useDebounce';

// Step 1: Company Basic Information
export const CompanyBasicInfo: FC<CompanyRegistrationStepProps> = ({ formData, updateFormData, errors, onNext }): JSX.Element => {
  const {companyDetails,} = useCompany();
  const [websiteValidationMessage, setWebsiteValidationMessage] = React.useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [websiteSuccessMessage, setWebsiteSuccessMessage] = React.useState<string | undefined>(undefined); // New state for success message
  const [localWebsite, setLocalWebsite] = React.useState<string>(formData.website || '');
  const debouncedWebsite = useDebounce<string>(localWebsite, 1000);

  React.useEffect(() => {
    if (debouncedWebsite) {
      validateWebsiteUrl(debouncedWebsite);
    } else {
      setWebsiteValidationMessage(undefined);
      setWebsiteSuccessMessage(undefined); // Clear success message too
    }
  }, [debouncedWebsite]);
  React.useEffect(() => {
    setWebsiteValidationMessage(undefined);
    setWebsiteSuccessMessage(undefined); // Clear success message too
  }, [localWebsite]);

  const validateWebsiteUrl = async (url: string) : Promise<{error:Boolean,msg:string}> => {
    if (!url) {
      setWebsiteValidationMessage(undefined);
      setWebsiteSuccessMessage(undefined);
      return {error:false,msg:""};
    }

    let formattedUrl = url;
    if (!/^https?:\/\//i.test(url)) {
      formattedUrl = `https://${url}`;
    }
    updateFormData({ website: formattedUrl});
    setLocalWebsite(formattedUrl);

    try {
      new URL(formattedUrl); // Basic URL format validation
      setWebsiteValidationMessage(undefined); // Clear previous errors if format is good

      // Attempt to fetch the URL to check if it's reachable
      // Note: For 'no-cors', response.ok will always be false, so we just check if fetch didn't throw an error.
      // A more robust check might involve a CORS-enabled endpoint or a backend proxy.
      await fetch(formattedUrl, { method: 'HEAD', mode: 'no-cors' });
      setWebsiteValidationMessage(undefined);
      setWebsiteSuccessMessage('Website link is valid'); // Set success message

      return {error : false , msg :'Website link is valid'}
    } catch (error) {
      setWebsiteValidationMessage('Please enter a valid and reachable website link');
      setWebsiteSuccessMessage(undefined); // Clear success message on error
      return {error : true , msg :'Please enter a valid website link'}
    }
  };

  const validateStep = async (): Promise<{ isValid: boolean; errors: { [key: string]: string } }> => {
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    if (formData.website && websiteValidationMessage) {
      newErrors.website = websiteValidationMessage;
    } else if (formData.website && websiteSuccessMessage == null) {
      const {error,msg} = await validateWebsiteUrl(formData.website);
      if(error){
        newErrors.website = 'Please enter a valid website URL';
      }
    }
    if (!formData.description?.trim()) {
      newErrors.description = 'Company description is required';
    }
    
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleNext = async (): Promise<void> => {
    setIsLoading(true)
    const validation = await validateStep();
    if (validation.isValid) {
      onNext(validation.errors);
    } else {
      onNext(validation.errors);
    }
    setIsLoading(false)
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-10">
        <div className="w-16 h-16 bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
          Company Information
        </h3>
        <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
          Tell us about your company and what makes it unique
        </p>
      </div>

      <div className="space-y-8">
        <Input
          label="Company Name"
          name="companyName"
          value={formData.companyName || ''}
          onChange={(e) => updateFormData({ companyName: e.target.value })}
          placeholder="Enter your company name"
          error={errors.companyName}
          required
        />

        <Input
          label="Company Website"
          name="website"
          type="url"
          value={localWebsite}
          onChange={(e) => {
            setLocalWebsite(e.target.value);
            updateFormData({ website: e.target.value });
          }}
          placeholder="https://www.company.com"
          error={websiteValidationMessage}
          success={websiteSuccessMessage}
        />

        <div className="space-y-3">
          <label className="block text-sm font-semibold text-gray-800 mb-3">
            Company Description <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={(e) => updateFormData({ description: e.target.value })}
              placeholder="Brief description of your company, what you do, and your mission. This helps us understand your business better."
              className={`block w-full px-4 py-4 border-2 rounded-2xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white resize-none ${
                errors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-200 hover:border-gray-300'
              }`}
              rows={5}
              required
            />
            <div className="absolute bottom-3 right-3 text-xs text-gray-400">
              {formData.description?.length || 0}/500
            </div>
          </div>
          {errors.description && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.description}
            </p>
          )}
        </div>

        <div className="flex justify-end pt-6">
          <Button 
            onClick={handleNext} 
            loading={isLoading}
            className="px-8 py-4 text-lg font-semibold bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen hover:from-primary-teaGreen hover:to-primary-lightGreen text-primary-black rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            Continue to Contact Details
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Button>
        </div>
      </div>
    </div>
  );
};

// Step 2: Contact Information
export const ContactInformation: FC<CompanyRegistrationStepProps> = ({ formData, updateFormData, errors, onNext, onPrev }): JSX.Element => {
  const validateStep = (): { isValid: boolean; errors: { [key: string]: string } } => {
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.contactName?.trim()) {
      newErrors.contactName = 'Contact name is required';
    }
    if (!formData.contactEmail?.trim()) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }
    if (!formData.contactPhone?.trim()) {
      newErrors.contactPhone = 'Contact phone is required';
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.contactPhone?.replace(/\s/g, '') || '')) {
      newErrors.contactPhone = 'Please enter a valid phone number';
    }
    if (!formData.address?.trim()) {
      newErrors.address = 'Address is required';
    }
    
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleNext = (): void => {
    const validation = validateStep();
    onNext(validation.errors);
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-10">
        <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </div>
        <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
          Contact Information
        </h3>
        <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
          How can we reach you for important updates?
        </p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Contact Person Name"
            name="contactName"
            value={formData.contactName || ''}
            onChange={(e) => updateFormData({ contactName: e.target.value })}
            placeholder="Full name"
            error={errors.contactName}
            required
          />

          <Input
            label="Contact Email"
            name="contactEmail"
            type="email"
            value={formData.contactEmail || ''}
            onChange={(e) => updateFormData({ contactEmail: e.target.value })}
            placeholder="contact@company.com"
            error={errors.contactEmail}
            required
          />
        </div>

        <Input
          label="Contact Phone"
          name="contactPhone"
          value={formData.contactPhone || ''}
          onChange={(e) => updateFormData({ contactPhone: e.target.value })}
          placeholder="+91 98765 43210"
          error={errors.contactPhone}
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Address <span className="text-red-500">*</span>
          </label>
          <textarea
            name="address"
            value={formData.address || ''}
            onChange={(e) => updateFormData({ address: e.target.value })}
            placeholder="Complete company address"
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen ${
              errors.address ? 'border-red-300' : 'border-gray-300'
            }`}
            rows={3}
            required
          />
          {errors.address && (
            <p className="mt-1 text-sm text-red-600">{errors.address}</p>
          )}
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={onPrev}>
            Previous
          </Button>
          <Button onClick={handleNext} className="px-8">
            Next Step
          </Button>
        </div>
      </div>
    </div>
  );
};

// Step 3: Business Details
export const BusinessDetails: FC<CompanyRegistrationStepProps> = ({ formData, updateFormData, errors, onNext, onPrev }): JSX.Element => {
  const validateStep = (): { isValid: boolean; errors: { [key: string]: string } } => {
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.industry) {
      newErrors.industry = 'Industry is required';
    }
    if (!formData.companySize) {
      newErrors.companySize = 'Company size is required';
    }
    if (!formData.businessType) {
      newErrors.businessType = 'Business type is required';
    }
    
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleNext = (): void => {
    const validation = validateStep();
    onNext(validation.errors);
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-10">
        <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0H8m8 0v6a2 2 0 01-2 2H10a2 2 0 01-2-2V6m8 0H8" />
          </svg>
        </div>
        <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
          Business Details
        </h3>
        <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
          Tell us about your business and industry
        </p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Select
              label="Industry"
              placeholder="Select your industry"
              options={[
                { value: '', label: 'Select industry' },
                { value: 'technology', label: 'Technology' },
                { value: 'finance', label: 'Finance & Banking' },
                { value: 'healthcare', label: 'Healthcare' },
                { value: 'education', label: 'Education' },
                { value: 'manufacturing', label: 'Manufacturing' },
                { value: 'retail', label: 'Retail & E-commerce' },
                { value: 'consulting', label: 'Consulting' },
                { value: 'real-estate', label: 'Real Estate' },
                { value: 'hospitality', label: 'Hospitality & Tourism' },
                { value: 'automotive', label: 'Automotive' },
                { value: 'energy', label: 'Energy & Utilities' },
                { value: 'media', label: 'Media & Entertainment' },
                { value: 'logistics', label: 'Logistics & Transportation' },
                { value: 'agriculture', label: 'Agriculture' },
                { value: 'construction', label: 'Construction' },
                { value: 'pharmaceuticals', label: 'Pharmaceuticals' },
                { value: 'telecommunications', label: 'Telecommunications' },
                { value: 'other', label: 'Other' }
              ]}
              value={formData.industry || ''}
              onChange={(value) => updateFormData({ industry: value })}
              required
              error={errors.industry}
            />
          </div>

          <div>
            <Select
              label="Company Size"
              placeholder="Select company size"
              options={[
                { value: '', label: 'Select company size' },
                { value: '1-10', label: '1-10 employees' },
                { value: '11-50', label: '11-50 employees' },
                { value: '51-200', label: '51-200 employees' },
                { value: '201-500', label: '201-500 employees' },
                { value: '501-1000', label: '501-1000 employees' },
                { value: '1000+', label: '1000+ employees' }
              ]}
              value={formData.companySize || ''}
              onChange={(value) => updateFormData({ companySize: value })}
              required
              error={errors.companySize}
            />
          </div>
        </div>

        <div>
          <Select
            label="Business Type"
            placeholder="Select business type"
            options={[
              { value: '', label: 'Select business type' },
              { value: 'private-limited', label: 'Private Limited Company' },
              { value: 'public-limited', label: 'Public Limited Company' },
              { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
              { value: 'partnership', label: 'Partnership' },
              { value: 'sole-proprietorship', label: 'Sole Proprietorship' },
              { value: 'ngo', label: 'NGO/Trust' },
              { value: 'startup', label: 'Startup' },
              { value: 'one-person-company', label: 'One Person Company (OPC)' },
              { value: 'section-8', label: 'Section 8 Company' }
            ]}
            value={formData.businessType || ''}
            onChange={(value) => updateFormData({ businessType: value })}
            required
            error={errors.businessType}
          />
        </div>

        <Input
          label="Founded Year"
          name="foundedYear"
          type="number"
          value={formData.foundedYear || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ foundedYear: parseInt(e.target.value) || undefined })}
          placeholder="2020"
          min="1900"
          max={new Date().getFullYear()}
        />

        <div className="flex justify-between">
          <Button variant="outline" onClick={onPrev}>
            Previous
          </Button>
          <Button onClick={handleNext} className="px-8">
            Next Step
          </Button>
        </div>
      </div>
    </div>
  );
};

// Step 4: Legal Documents
export const LegalDocuments: FC<CompanyRegistrationStepProps> = ({ formData, updateFormData, errors, onNext, onPrev }): JSX.Element => {
  const {companyDetails, updateCompanyAttachments} = useCompany()
  const validateStep = (): { isValid: boolean; errors: { [key: string]: string } } => {
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.registrationType) {
      newErrors.registrationType = 'Registration type is required';
    }
    
    // Validate based on selected registration type
    if (formData.registrationType === 'gstin') {
      if (!formData.gstNumber?.trim()) {
        newErrors.gstNumber = 'GST number is required';
      } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber)) {
        newErrors.gstNumber = 'Please enter a valid GST number';
      }
    } else if (formData.registrationType === 'pan') {
      if (!formData.panNumber?.trim()) {
        newErrors.panNumber = 'PAN number is required';
      } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)) {
        newErrors.panNumber = 'Please enter a valid PAN number';
      }
    } else if (formData.registrationType === 'msme') {
      if (!formData.msmeNumber?.trim()) {
        newErrors.msmeNumber = 'MSME number is required';
      }
    }
    
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleNext = (): void => {
    const validation = validateStep();
    onNext(validation.errors);
  };
  const handleAttachmentUpload = async (newAttachment: Attachments | null) => {
      if (!newAttachment || !newAttachment.typeId) {
        console.error("Uploaded attachment is missing or missing typeId:", newAttachment);
        // setToast({ message: "Uploaded file is missing or missing type information.", type: "error" });
        return;
      }
  
      // Update local profileData state
      // setProfileData((prevProfileData) => {
      //   const existingAttachments = prevProfileData.attachments || [];
      //   const updatedAttachments = existingAttachments
      //     .filter((att) => att.typeId !== newAttachment.typeId) // Remove old attachment of the same type
      //     .concat(newAttachment); // Add the new attachment
  
      //   return {
      //     ...prevProfileData,
      //     attachments: updatedAttachments,
      //   };
      // });
  
      // Update companyDetails in context
      // handleNext();
      updateCompanyAttachments(newAttachment);
    };
  return (
    <div className="space-y-8">
      <div className="text-center mb-10">
        <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
          Verification Document
        </h3>
        <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
          Provide your company's legal information for verification
        </p>
      </div>

      <div className="space-y-6">
        {/* Modern Registration Type Dropdown */}
        <div>
          <Select
            label="Document Type"
            placeholder="Select your registration type"
            options={[
              { value: '', label: 'Select your Document type' },
              { value: 'msme', label: 'MSME (Micro, Small & Medium Enterprises)' },
              { value: 'gstin', label: 'GSTIN (Goods & Services Tax)' },
              { value: 'pan', label: 'PAN (Permanent Account Number)' }
            ]}
            value={formData.registrationType || ''}
            onChange={(value) => {
              if (value === "gstin" || value === "pan" || value === "msme") {
                updateFormData({ registrationType: value });
              }
            }}
            required
            error={errors.registrationType}
          />
        </div>

        {/* Conditional fields based on registration type */}
        {formData.registrationType === 'gstin' && (
          <Input
            label="GST Number"
            name="gstNumber"
            value={formData.gstNumber || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ gstNumber: e.target.value.toUpperCase() })}
            placeholder="22AAAAA0000A1Z5"
            error={errors.gstNumber}
            required
          />
        )}

        {formData.registrationType === 'pan' && (
          <Input
            label="PAN Number"
            name="panNumber"
            value={formData.panNumber || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ panNumber: e.target.value.toUpperCase() })}
            placeholder="AAAAA0000A"
            error={errors.panNumber}
            required
          />
        )}

        {formData.registrationType === 'msme' && (
          <Input
            label="MSME Number"
            name="msmeNumber"
            value={formData.msmeNumber || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ msmeNumber: e.target.value.toUpperCase() })}
            placeholder="Enter MSME registration number"
            error={errors.msmeNumber}
            required
          />
        )}

        {/* Document Upload Section - Redesigned to match User Profile style */}
        {formData.registrationType && (
          <div className="space-y-8">
            <div className="text-center">
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Document Upload</h4>
              <p className="text-gray-600">Upload supporting documents to speed up verification</p>
            </div>
            
            <div className="w-full">
              {/* Dynamic document fields based on registration type */}
              {formData.registrationType === 'gstin' && (
                <FileUploader
                  label="GST Registration Certificate"
                  name="gstCertificate"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={10 * 1024 * 1024} // 10MB
                  // onFileSelect={(file) => updateFormData({ gstCertificate: file })}
                  attachment={companyDetails?.attachments?.find(a=>a.typeId=='CompanyDocumentGST')}
                  referenceId={companyDetails?.companyId || ''}
                  onFileUploaded={handleAttachmentUpload}
                  // currentFile={formData.gstCertificate}
                  description="Upload your GST registration certificate (one file)"
                  uploadType='CompanyDocumentGST'
                />
              )}

              {formData.registrationType === 'pan' && (
                <FileUploader
                  label="PAN Card Copy"
                  name="panCard"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={10 * 1024 * 1024} // 10MB
                  // onFileSelect={(file) => updateFormData({ panCard: file })}
                  attachment={companyDetails?.attachments?.find(a=>a.typeId=='CompanyDocumentPAN')}
                  referenceId={companyDetails?.companyId || ''}
                  onFileUploaded={handleAttachmentUpload}
                  // currentFile={formData.panCard}
                  description="Upload a clear copy of your PAN card"
                  uploadType='CompanyDocumentPAN'
                />
              )}

              {formData.registrationType === 'msme' && (
                <FileUploader
                  label="MSME Registration Certificate"
                  name="msmeCertificate"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={10 * 1024 * 1024} // 10MB
                  // onFileSelect={(file) => updateFormData({ msmeCertificate: file })}
                  attachment={companyDetails?.attachments?.find(a=>a.typeId=='CompanyDocumentMSME')}
                  referenceId={companyDetails?.companyId || ''}
                  onFileUploaded={handleAttachmentUpload}
                  // currentFile={formData.msmeCertificate}
                  description="Upload your MSME registration certificate"
                  uploadType='CompanyDocumentMSME'
                />
              )}
            </div>
            
            {/* Upload Tips - Simplified */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <h6 className="font-medium text-green-900">Upload Tips</h6>
                  <p className="text-sm text-green-700 mt-1">
                    Use clear, high-quality scans. Max 10MB per file. PDF, JPG, PNG formats accepted. Original documents preferred.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between">
          <Button variant="outline" onClick={onPrev}>
            Previous
          </Button>
          <Button onClick={handleNext} className="px-8">
            Next Step
          </Button>
        </div>
      </div>
    </div>
  );
};

// Step 5: Review and Submit
export const ReviewAndSubmit: FC<CompanyRegistrationStepProps & { onSubmit: () => void }> = ({ formData, onPrev, onSubmit, isSubmitting }): JSX.Element => {
  return (
    <div className="space-y-8">
      <div className="text-center mb-10">
        <div className="w-16 h-16 bg-gradient-to-r from-primary-teaGreen to-primary-lightGreen rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        </div>
        <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
          Review Your Information
        </h3>
        <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
          Please review all details before submitting your registration
        </p>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg space-y-6">
        {/* Company Information */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 border-b pb-2">Company Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Company Name:</span>
              <p className="text-gray-900">{formData.companyName || 'Not provided'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Website:</span>
              <p className="text-gray-900">{formData.website || 'Not provided'}</p>
            </div>
            <div className="md:col-span-2">
              <span className="font-medium text-gray-700">Description:</span>
              <p className="text-gray-900">{formData.description || 'Not provided'}</p>
            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 border-b pb-2">Contact Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Contact Person:</span>
              <p className="text-gray-900">{formData.contactName || 'Not provided'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Email:</span>
              <p className="text-gray-900">{formData.contactEmail || 'Not provided'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Phone:</span>
              <p className="text-gray-900">{formData.contactPhone || 'Not provided'}</p>
            </div>
            <div className="md:col-span-2">
              <span className="font-medium text-gray-700">Address:</span>
              <p className="text-gray-900">{formData.address || 'Not provided'}</p>
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 border-b pb-2">Business Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Industry:</span>
              <p className="text-gray-900">{formData.industry || 'Not provided'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Company Size:</span>
              <p className="text-gray-900">{formData.companySize || 'Not provided'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Business Type:</span>
              <p className="text-gray-900">{formData.businessType || 'Not provided'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Founded Year:</span>
              <p className="text-gray-900">{formData.foundedYear || 'Not provided'}</p>
            </div>
          </div>
        </div>

        {/* Legal Information */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 border-b pb-2">Legal Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Registration Type:</span>
              <p className="text-gray-900">{formData.registrationType ? formData.registrationType.toUpperCase() : 'Not provided'}</p>
            </div>
            {formData.registrationType === 'gstin' && (
              <div>
                <span className="font-medium text-gray-700">GST Number:</span>
                <p className="text-gray-900">{formData.gstNumber || 'Not provided'}</p>
              </div>
            )}
            {formData.registrationType === 'pan' && (
              <div>
                <span className="font-medium text-gray-700">PAN Number:</span>
                <p className="text-gray-900">{formData.panNumber || 'Not provided'}</p>
              </div>
            )}
            {formData.registrationType === 'msme' && (
              <div>
                <span className="font-medium text-gray-700">MSME Number:</span>
                <p className="text-gray-900">{formData.msmeNumber || 'Not provided'}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Important Notice
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                By submitting this form, you confirm that all information provided is accurate and complete. 
                Your company registration will be reviewed by our team and you will receive approval within 24-48 hours.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev}>
          Previous
        </Button>
        <Button onClick={onSubmit} loading={isSubmitting} className="px-8">
          {isSubmitting ? 'Submitting...' : 'Submit Registration'}
        </Button>
      </div>
    </div>
  );
};
